import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";

import { useState } from "react";
import { GlobalState } from "../../../context/GlobalContext";

import axios from "axios";

import SupplierInfoModal from "./SupplierInfoModal/SupplierInfoModal";
import ClearRounded from "@mui/icons-material/ClearRounded";
import { DownloadRounded } from "@mui/icons-material";
import FileSaver from "file-saver";
import {
	SupplierModalHeader,
	SupplierPageHeader,
	SupplierStatusChip,
	supplierModalPaperProps,
	supplierUi,
} from "../shared/supplierUi";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";

const statusCell = (label) => <SupplierStatusChip label={label} />;

const supplierTablecolumns = [
	{
		key: "product_division",
		name: "Product Divisions",
		width: 140,
		headerAlign: "center",
		align: "center",
	},
	{
		key: "user_email",
		name: "Contact Person",
		width: 160,
		headerAlign: "center",
		align: "center",
		flex: 1,
		minWidth: 140,
	},
	{
		key: "company_name",
		name: "Company",
		width: 160,
		headerAlign: "center",
		align: "center",
		flex: 1,
		minWidth: 140,
	},
	{ key: "company_type", name: "Type", width: 110, headerAlign: "center", align: "center" },
	{ key: "supplier_nature", name: "Nature", width: 130, headerAlign: "center", align: "center" },
	{ key: "industry_type", name: "Industry Type", width: 130, headerAlign: "center", align: "center" },
	{ key: "capacity_for_thor", name: "Capacity THOR", width: 120, headerAlign: "center", align: "center" },
	{ key: "monthly_capacity", name: "Monthly Capacity", width: 130, headerAlign: "center", align: "center" },
	{
		key: "finance_approved",
		name: "Finance Approval",
		width: 145,
		headerAlign: "center",
		align: "center",
		renderCell: ({ row }) => {
			const label = row.finance_approved
				? "Approved"
				: row.finance_approved === 0 && row.finance_remark
				? "Rejected"
				: "Pending";
			return statusCell(label);
		},
		valueGetter: ({ row }) =>
			row.finance_approved ? "Approved" : row.finance_approved === 0 && row.finance_remark ? "Rejected" : "Pending",
	},
	{
		key: "is_approved",
		name: "Final Approval",
		width: 130,
		headerAlign: "center",
		align: "center",
		renderCell: ({ row }) => statusCell(row.is_approved ? "Approved" : "Pending"),
		valueGetter: ({ row }) => (row.is_approved ? "Approved" : "Pending"),
	},
	{
		key: "is_active",
		name: "Status",
		width: 120,
		headerAlign: "center",
		align: "center",
		renderCell: ({ row }) => statusCell(row.is_active ? "Active" : "inactive"),
		valueGetter: ({ value }) => `${value || ""}`,
	},
];

const SupplierDashboard = () => {
	const [pageData, setPageData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [filterObj, setFilterObj] = useState({});

	const [selectedSupplier, setSelectedSupplier] = useState();
	const [modalEditable, setModalEditable] = useState(true);
	const [modalOpen, setModalOpen] = useState(false);

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();

	const handleEditModalClose = () => {
		setModalEditable(true);
		setModalOpen(false);
	};

	const getServerSideData = async (limit, offset, filters) => {
		const res = await axios.post(`/api/supplier/get`, {
			limit,
			offset,
			filter: filters,
		});
		return res.data.result;
	};

	const handleGridRowClick = ({ row }) => {
		setModalOpen(true);
		setSelectedSupplier(row);
	};

	const handleSnackBarCommon = (severity, message) => {
		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			message: message,
			severity: severity,
			anchorOrigin: { horizontal: "center", vertical: "top" },
		});
	};

	const handleDownloadClick = async () => {
		setLoadingOpen(true);
		try {
			await axios.get("/api/supplier/download_suppliers", { responseType: "blob" }).then((res) => {
				const url = URL.createObjectURL(res.data);
				FileSaver.saveAs(url, "All_suppliers.xlsx");
				handleSnackBarCommon("success", "Successfully download the file");
			});
		} catch (error) {
			handleSnackBarCommon("error", "Failed to download file");
		}
		setLoadingOpen(false);
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 64px)",
				bgcolor: "#F9FAFB",
				px: { xs: 1.5, md: 3 },
				py: { xs: 2, md: 3 },
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
			}}>
			<Box
				sx={{
					width: "100%",
					maxWidth: 1400,
					mx: "auto",
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
				}}>
				<SupplierPageHeader
					title="Supplier Dashboard"
					subtitle="Review onboarding status and open a supplier profile"
					sx={{ mb: 2, flexShrink: 0 }}
					actions={
						<Button
							startIcon={<DownloadRounded />}
							variant="contained"
							onClick={handleDownloadClick}
							sx={{
								textTransform: "none",
								fontWeight: 600,
								borderRadius: 1.5,
								boxShadow: "none",
								"&:hover": { boxShadow: "none" },
							}}>
							Download
						</Button>
					}
				/>

				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						width: "100%",
						border: `1px solid ${supplierUi.border}`,
						borderRadius: supplierUi.radius,
						overflow: "hidden",
						bgcolor: "#fff",
					}}>
					<TanstackDatagrid
						columns={supplierTablecolumns}
						pageData={pageData}
						setPageData={setPageData}
						rowCount={rowCount}
						setRowCount={setRowCount}
						filterObj={filterObj}
						setFilterObj={setFilterObj}
						getServerSideData={getServerSideData}
						rowsPerPage={50}
						emptyMessage="No suppliers found"
						handleRowClick={handleGridRowClick}
						getRowId={(row) => row.id}
						headerRowStyle={{ background: "#FAFAFA" }}
						rowHeight={48}
						headerHeight={48}
					/>
				</Box>
			</Box>

			<Dialog
				open={modalOpen}
				onClose={handleEditModalClose}
				scroll="body"
				maxWidth="md"
				fullWidth
				PaperProps={supplierModalPaperProps}>
				<Box sx={{ position: "relative" }}>
					<SupplierModalHeader
						title="Supplier information"
						subtitle={selectedSupplier?.company_name || "Update payment terms and status"}
					/>
					<IconButton
						onClick={handleEditModalClose}
						size="small"
						sx={{ position: "absolute", top: 16, right: 16 }}>
						<ClearRounded fontSize="small" />
					</IconButton>
				</Box>
				<Box sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
					<SupplierInfoModal
						modalEditable={modalEditable}
						allSuppliers={pageData}
						setAllSuppliers={setPageData}
						selectedSupplier={selectedSupplier}
						setSelectedSupplier={setSelectedSupplier}
						setModalOpen={setModalOpen}
						setModalEditable={setModalEditable}
					/>
				</Box>
			</Dialog>
		</Box>
	);
};

export default SupplierDashboard;
